import { CollapsibleContent } from '@seen/ui'
import { Children, type JSX, type ReactNode } from 'react'
import { useFacesheetCard } from '#src/components/patient/detail-content/facesheet/FacesheetCardContext.ts'

export interface FacesheetCardContentProps {
  children: ReactNode
}

// Everything below the header row. On a card that declared itself
// non-collapsible this renders nothing of its own, leaving the content a
// direct child of the card; otherwise it is the
// Collapsible content the triangle folds away, repeating the card's own gap-2
// so the nesting costs no spacing.
//
// Radix unmounts closed content, so a collapse tears the card's subtree down
// and an expand rebuilds it: the questionnaire cards drop and re-create a
// ProseMirror instance per rich-text item, and a card re-expanded past the
// 60s query staleTime refetches. Both are the price of collapsing rather than
// hiding, and both are paid only on a user toggle. Anything that must survive
// one — a dialog the card owns — belongs outside this slot.
export function FacesheetCardContent({
  children,
}: FacesheetCardContentProps): JSX.Element | null {
  const { collapsible } = useFacesheetCard()

  // `false && <Body />` still passes a children array of booleans; toArray
  // drops those so an empty card does not leave a gap under the header.
  if (Children.toArray(children).length === 0) {
    return null
  }

  if (!collapsible) {
    return <>{children}</>
  }

  return (
    <CollapsibleContent className="flex flex-col gap-2">
      {children}
    </CollapsibleContent>
  )
}
